import React from 'react'
import * as ROUTES from 'constants/routes'
import { Box, Link, Table, Text, Tooltip } from '@workwhile/ui'
import { ColumnDef } from '@tanstack/react-table'
import { formatInTimeZone } from 'date-fns-tz'
import { JetAlertItem } from 'api/admin/jet/listJetAlerts'
import { JET_ALERT_TYPE_KEY_HINTS } from 'pages/Jet/shared/constants/alertType'
import { JetAlertTypeBadge } from './JetAlertTypeBadge'
import { JetAlertStatusBadge } from './JetAlertStatusBadge'

// jet_alert timestamps are UTC instants with no per-row timezone, so they are
// rendered in WorkWhile HQ time (mirrors the backend ADMIN_TZ).
const DISPLAY_TZ = 'America/Los_Angeles'

const formatDateTime = (value: string) =>
  formatInTimeZone(value, DISPLAY_TZ, 'MMM d, yyyy h:mm a z')

const formatOrDash = (value: string | null) =>
  value ? formatDateTime(value) : '-'

// The raw key (shift id, assignment id, group key, store id, or position id);
// its meaning per alert type is shown as a tooltip. Shift/worker deep-links live
// in their own columns, resolved server-side from the key.
const KeyCell = ({ item }: { item: JetAlertItem }) => {
  const hint = JET_ALERT_TYPE_KEY_HINTS[item.alertType]

  return (
    <Tooltip content={hint}>
      <Text style={{ whiteSpace: 'nowrap' }}>{item.key}</Text>
    </Tooltip>
  )
}

// Shift/worker are only resolved (server-side) for alert types whose key points
// at them, so these columns render a link when present and a dash otherwise.
const ShiftCell = ({ shiftId }: { shiftId: number | null }) => {
  if (shiftId === null) {
    return <Text>-</Text>
  }
  return (
    <Link
      to={ROUTES.SHIFT_DETAIL.replace(':shiftId', String(shiftId))}
      target="_blank"
    >
      {shiftId}
    </Link>
  )
}

const WorkerCell = ({
  workerId,
  workerName,
}: {
  workerId: number | null
  workerName: string | null
}) => {
  if (workerId === null) {
    return <Text>-</Text>
  }
  return (
    <Link
      to={ROUTES.USER_DETAIL.replace(':userId', String(workerId))}
      target="_blank"
      style={{ whiteSpace: 'nowrap' }}
    >
      {workerName ? `${workerName} (${workerId})` : `#${workerId}`}
    </Link>
  )
}

const columns: ColumnDef<JetAlertItem>[] = [
  {
    header: 'Instance',
    accessorKey: 'jetInstanceName',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {row.original.jetInstanceName}
      </Text>
    ),
  },
  {
    header: 'Alert type',
    accessorKey: 'alertType',
    cell: ({ row }) => <JetAlertTypeBadge alertType={row.original.alertType} />,
  },
  {
    header: 'Key',
    accessorKey: 'key',
    cell: ({ row }) => <KeyCell item={row.original} />,
  },
  {
    header: 'Shift ID',
    accessorKey: 'shiftId',
    cell: ({ row }) => <ShiftCell shiftId={row.original.shiftId} />,
  },
  {
    header: 'Worker',
    accessorKey: 'workerId',
    cell: ({ row }) => (
      <WorkerCell
        workerId={row.original.workerId}
        workerName={row.original.workerName}
      />
    ),
  },
  {
    header: 'WSA status',
    accessorKey: 'wsaStatus',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {row.original.wsaStatus ?? '-'}
      </Text>
    ),
  },
  {
    header: 'Status',
    cell: ({ row }) => (
      <JetAlertStatusBadge resolvedAt={row.original.resolvedAt} />
    ),
  },
  {
    header: 'Last alerted',
    accessorKey: 'lastAlertedAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.lastAlertedAt)}
      </Text>
    ),
  },
  {
    header: 'Created',
    accessorKey: 'createdAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatDateTime(row.original.createdAt)}
      </Text>
    ),
  },
  {
    header: 'Resolved',
    accessorKey: 'resolvedAt',
    cell: ({ row }) => (
      <Text style={{ whiteSpace: 'nowrap' }}>
        {formatOrDash(row.original.resolvedAt)}
      </Text>
    ),
  },
]

type JetAlertsTableProps = {
  items: JetAlertItem[]
}

export const JetAlertsTable = ({ items }: JetAlertsTableProps) => {
  return (
    <Box overflowX="auto">
      <Table columns={columns} data={items} textVerticalAlign="top" />
    </Box>
  )
}
